import React, { useEffect, useState } from 'react';
import { AppointmentStatus } from '../../types/apiGeneratedTypes';
import { statusOptions, statusTranslations } from '../../utils/constants';
import { updateAppointment } from '../../api/appointments';

type Props = {
  appointmentId: number;
  status: AppointmentStatus;
  disabled?: boolean;
  onStatusSaved: (newStatus: AppointmentStatus) => void;
};

const knownStatusValues = new Set(
  statusOptions.map((o) => o.value as AppointmentStatus)
);

const AppointmentInlineStatusSelect: React.FC<Props> = ({
  appointmentId,
  status,
  disabled = false,
  onStatusSaved
}) => {
  const [localStatus, setLocalStatus] = useState<AppointmentStatus>(status);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setLocalStatus(status);
  }, [status]);

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value as AppointmentStatus;
    if (next === localStatus) return;
    const previous = localStatus;
    setLocalStatus(next);
    setPending(true);
    try {
      await updateAppointment({
        appointment_id: appointmentId,
        status: next
      });
      onStatusSaved(next);
    } catch {
      setLocalStatus(previous);
    } finally {
      setPending(false);
    }
  };

  const showUnknownOption = !knownStatusValues.has(localStatus);

  return (
    <select
      className='appointment-inline-status-select'
      value={localStatus}
      onChange={handleChange}
      disabled={disabled || pending}
      aria-label='Appointment status'
    >
      {showUnknownOption && (
        <option value={localStatus}>
          {statusTranslations[
            localStatus as keyof typeof statusTranslations
          ] ?? String(localStatus)}
        </option>
      )}
      {statusOptions.map(({ value: v, label }) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
};

export default AppointmentInlineStatusSelect;
